import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit
} from '@angular/core';
import {
  CheckboxesComponent,
  CheckboxNode,
  ReactiveCheckboxWatcherService
} from '@jeppesen-foreflight/jmkw-toolkit';
import { ExtendedMap } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { map, Observable, startWith, Subject, switchMap, take } from 'rxjs';
import { pluginsToCheckboxNodeMap } from '../../../plugin-utils';
import { TwcPluginService } from '../../plugins/twc.plugin.service';

@Component({
  selector: 'jmkw-twc-checkboxes-popout',
  standalone: true,
  imports: [CommonModule, CheckboxesComponent],
  templateUrl: './twc-checkboxes-popout.component.html',
  styleUrls: ['./twc-checkboxes-popout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TwcCheckboxesPopoutComponent implements OnInit {
  automationKey = 'weather-popout';
  private readonly _twcPluginService = inject(TwcPluginService);
  private readonly _logger = inject(NGXLogger);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _reactiveCheckboxWatcher = inject(
    ReactiveCheckboxWatcherService
  );

  // Manual refresh trigger for when layer visibility changes
  private readonly _refreshTrigger$ = new Subject<void>();

  checkboxNodeMap$: Observable<ExtendedMap<CheckboxNode>> =
    this._refreshTrigger$.pipe(
      startWith(void 0), // Start immediately
      switchMap(() => this._twcPluginService.weatherPlugin$),
      map((twcPlugin) => pluginsToCheckboxNodeMap(twcPlugin.children))
    );

  ngOnInit() {
    // Set up reactive monitoring for layer visibility changes
    // We only need to do this once when the plugin is available
    this._twcPluginService.weatherPlugin$
      .pipe(
        take(1) // Take first emitted value to set up watchers once
      )
      .subscribe((twcPlugin) => {
        if (twcPlugin?.children) {
          this.setupLayerVisibilityWatchers(twcPlugin.children);
        }
      });
  }

  private setupLayerVisibilityWatchers(plugins: Map<string, any>) {
    this._reactiveCheckboxWatcher.setupWatchers(
      plugins.values(),
      (plugin: any) => plugin.layer?.visible,
      (plugin: any) =>
        plugin.children instanceof Map
          ? plugin.children.values()
          : plugin.children,
      () => {
        this._refreshTrigger$.next();
      },
      this._destroyRef
    );
  }

  onChange(checkboxNodes: CheckboxNode[]) {
    this._logger.trace(
      `[TwcCheckboxesPopoutComponent] onChange:`,
      checkboxNodes
    );
  }
}
